import { useMemo } from 'react'

import { useQuery } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import type { FragmentOf, ResultOf } from '~publish/graphql'
import { isService } from '~publish/helpers/channels'
import { sanitizeNullableArray } from '~publish/helpers/typeGuards'
import { canPerform } from '~publish/utils/permissions/canPerform'

export const MainSidebarChannelFragment = graphql(`
  fragment MainSidebarChannelFragment on Channel @_unmask {
    id
    name
    displayName
    metadata {
      ... on BlueskyMetadata {
        __typename
        serverUrl
      }
    }
    service
    type
    avatar
    timezone
    serverUrl
    locationData {
      location
    }
    isLocked
    isDisconnected
    isQueuePaused
    apiVersion
    products
    allowedActions
  }
`)

export const GetMainSidebarInfo = graphql(
  `
    query GetMainSidebarInfo {
      # TODO: channels should be a top-level query
      account {
        id
        currentOrganization {
          id
          channels {
            ...MainSidebarChannelFragment
          }
          onboarding {
            status
            responses {
              stepSlug
              selectedOptions
              skipped
            }
          }
        }
      }
    }
  `,
  [MainSidebarChannelFragment],
)

export type MainSidebarChannel = FragmentOf<typeof MainSidebarChannelFragment>

/** A channel's social service, derived from the sidebar channel selection. */
export type Service = ResultOf<typeof MainSidebarChannelFragment>['service']

const TEAM_CONTEXT_SLUGS = new Set([
  'company-marketing-team',
  'marketing-agency',
])

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useChannels() {
  // This query must stay eligible for `client.refetchQueries`: org
  // switching refetches it (`refetchQueries: [GetMainSidebarInfo]`), and
  // any `cache-only` fetch-policy window would silently exclude it from
  // that, keeping the previous org's channels on screen. `cache-first`
  // already avoids network requests while the cached result is complete.
  const { data, loading, ...rest } = useQuery(GetMainSidebarInfo, {
    fetchPolicy: 'cache-first',
  })

  const rawChannels = data?.account?.currentOrganization?.channels
  const { channels, lockedChannels } = useMemo(() => {
    const allChannels = sanitizeNullableArray(rawChannels)
    return {
      channels: allChannels.filter(
        (ch) => !ch.isLocked || ch.service === 'startPage',
      ),
      lockedChannels: allChannels.filter(
        (ch) => ch.isLocked && ch.service !== 'startPage',
      ),
    }
  }, [rawChannels])

  const onboardingResponses =
    data?.account?.currentOrganization?.onboarding?.responses
  const channelsFocusResponse = onboardingResponses?.find(
    (r) => r.stepSlug === 'channels-in-focus' && !r.skipped,
  )
  const onboardingChannelFocus: Service[] | null = channelsFocusResponse
    ? channelsFocusResponse.selectedOptions.filter(isService)
    : null

  const describeYourselfResponse = onboardingResponses?.find(
    (r) => r.stepSlug === 'describe-yourself' && !r.skipped,
  )
  const isTeamContext =
    describeYourselfResponse?.selectedOptions.some((opt) =>
      TEAM_CONTEXT_SLUGS.has(opt),
    ) ?? false

  return {
    channels,
    lockedChannels,
    isTeamContext,
    loading: loading && !data,
    onboardingChannelFocus,
    ...rest,
  }
}

/**
 * Whether the current user can create posts on at least one sidebar channel.
 *
 * `manageUpdates` is granted for `fullPosting` / `requiresApproval` publish
 * access and denied for `viewOnly` / `none`, so this is `false` exactly when
 * every channel the user can see is view-only or inaccessible.
 */
export function useCanPostOnAnyChannel(): boolean {
  const { channels } = useChannels()
  return channels.some((channel) => canPerform(channel, 'manageUpdates'))
}
